import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import type { Identifier } from "ra-core";
import {
  useDataProvider,
  useGetList,
  useGetOne,
  useListController,
  useLocaleState,
  useNotify,
  useTranslate,
  RecordContextProvider,
  RecordRepresentation,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";

import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { PageFrame } from "@/tenax/procedures/ProcedurePages";
import { nativeSelectClass } from "@/tenax/tasks/taskData";
import {
  AGREEMENT_TYPES,
  SAVED_STATUSES,
  useKitVersions,
  usePackages,
  type Agreement,
  type AgreementCheck,
  type FirstOrder,
  type FreeItem,
  type Obligation,
} from "./agreementData";
import { AgreementForm, type AgreementFormValues } from "./AgreementForm";
import { AgreementWarnings } from "./AgreementWarnings";
import { CodeChip, ReferralCodes } from "./ReferralCodes";
import { useAgreementCodes } from "./useReferralCodes";
import { useAgreementWarnings } from "./useAgreementWarnings";

const useDay = () => {
  const [locale] = useLocaleState();
  return (value?: string | null) =>
    value
      ? new Date(`${value}T12:00:00`).toLocaleDateString(locale, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "—";
};

const BackLink = ({ to }: { to: string }) => {
  const translate = useTranslate();
  return (
    <Button variant="ghost" size="icon" asChild>
      <Link to={to} aria-label={translate("ra.action.back")}>
        <ArrowLeft className="size-5" />
      </Link>
    </Button>
  );
};

// List of the agreements (desktop menu "Accordi"), with their warnings
export const AgreementList = () => {
  const translate = useTranslate();
  const list = useListController<Agreement>({
    resource: "agreements",
    sort: { field: "id", order: "DESC" },
    perPage: 100,
    disableSyncWithLocation: true,
    storeKey: "tenax.agreements.list",
  });
  const warningsOf = useAgreementWarnings(
    (list.data ?? []).map((item) => item.id),
  );
  const setFilter = (name: string, value: string) =>
    list.setFilters(
      { ...list.filterValues, [name]: value || undefined },
      list.displayedFilters,
    );

  return (
    <PageFrame
      title={translate("resources.agreements.name", { smart_count: 2 })}
    >
      <div className="flex flex-wrap items-end gap-3">
        {(
          [
            ["type", AGREEMENT_TYPES],
            ["status", SAVED_STATUSES],
          ] as const
        ).map(([name, values]) => (
          <div key={name} className="flex flex-col gap-1">
            <Label htmlFor={`agreements-${name}`}>
              {translate(`resources.agreements.fields.${name}`)}
            </Label>
            <select
              id={`agreements-${name}`}
              className={nativeSelectClass}
              value={list.filterValues[name] ?? ""}
              onChange={(event) => setFilter(name, event.target.value)}
            >
              <option value="">{translate("tenax.agreements.all")}</option>
              {values.map((value) => (
                <option key={value} value={value}>
                  {translate(`tenax.agreements.${name}.${value}`)}
                </option>
              ))}
            </select>
          </div>
        ))}
        <span className="flex-1" />
        <Button asChild>
          <Link to="/agreements/create">
            {translate("tenax.agreements.new")}
          </Link>
        </Button>
      </div>
      <AgreementRows agreements={list.data ?? []} warningsOf={warningsOf} />
    </PageFrame>
  );
};

export const AgreementRows = ({
  agreements,
  warningsOf,
}: {
  agreements: Agreement[];
  warningsOf: ReturnType<typeof useAgreementWarnings>;
}) => {
  const translate = useTranslate();
  const codesOf = useAgreementCodes(agreements.map((item) => item.id));
  if (!agreements.length) {
    return (
      <p className="text-sm text-muted-foreground">
        {translate("tenax.agreements.empty")}
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {agreements.map((agreement) => (
        <li
          key={agreement.id}
          aria-label={agreement.title}
          className="rounded-md border px-3 py-2"
        >
          <Link
            to={`/agreements/${agreement.id}/show`}
            className="flex flex-col gap-1"
          >
            <span className="text-sm font-medium">{agreement.title}</span>
            <span className="text-xs text-muted-foreground">
              {translate(`tenax.agreements.type.${agreement.type}`)} ·{" "}
              {translate(`tenax.agreements.status.${agreement.status}`)}
            </span>
            <AgreementWarnings warnings={warningsOf(agreement.id)} />
          </Link>
          {codesOf(agreement.id).length ? (
            <div
              role="group"
              aria-label={translate("tenax.agreements.codes.title")}
              className="mt-1 flex flex-wrap gap-2"
            >
              {codesOf(agreement.id).map((code) => (
                <CodeChip key={code.id} code={code.code} />
              ))}
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
};

const Fact = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-sm">{children}</span>
  </div>
);

export const AgreementShow = () => {
  const translate = useTranslate();
  const formatDay = useDay();
  const { id } = useParams();
  const { data: agreement } = useGetOne<Agreement>("agreements", { id: id! });
  const warningsOf = useAgreementWarnings(id ? [Number(id)] : []);
  const { data: check } = useGetOne<AgreementCheck>(
    "agreement_checks",
    { id: id! },
    { enabled: !!id },
  );
  const { data: firstOrders = [] } = useGetList<FirstOrder>(
    "agreement_first_order",
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "ASC" },
      filter: { id },
    },
    { enabled: !!id },
  );
  const { data: packages = [] } = usePackages();
  const { data: kits = [] } = useKitVersions();
  const { data: freeItems = [] } = useGetList<FreeItem & { id: number }>(
    "agreement_free_items",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "id", order: "ASC" },
      filter: { agreement_id: id },
    },
  );
  if (!agreement) return null;
  const field = (name: string) =>
    translate(`resources.agreements.fields.${name}`);
  const firstOrder = firstOrders[0];

  return (
    <PageFrame title={agreement.title} back={<BackLink to="/agreements" />}>
      <RecordContextProvider value={agreement}>
        <Card>
          <CardContent className="flex flex-col gap-4">
            <AgreementWarnings warnings={warningsOf(agreement.id)} />
            <ReferralCodes
              agreementId={agreement.id}
              contactId={agreement.contact_id}
            />
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <Fact label={field("type")}>
                {translate(`tenax.agreements.type.${agreement.type}`)}
              </Fact>
              <Fact label={field("status")}>
                {translate(`tenax.agreements.status.${agreement.status}`)}
              </Fact>
              <Fact label={field("company_id")}>
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  link="show"
                >
                  <TextField source="name" />
                </ReferenceField>
              </Fact>
              <Fact label={field("contact_id")}>
                <ReferenceField
                  source="contact_id"
                  reference="contacts"
                  link="show"
                >
                  <RecordRepresentation />
                </ReferenceField>
              </Fact>
              <Fact label={field("signed_at")}>
                {formatDay(agreement.signed_at)}
              </Fact>
              <Fact label={field("start_date")}>
                {formatDay(agreement.start_date)}
              </Fact>
              <Fact label={field("end_date")}>
                {formatDay(agreement.end_date)}
              </Fact>
              <Fact label={field("renewal")}>
                {translate(`tenax.agreements.renewal.${agreement.renewal}`)}
                {check?.notice_deadline && agreement.end_date
                  ? ` · ${translate("tenax.agreements.notice_by", {
                      date: formatDay(check.notice_deadline),
                    })}`
                  : ""}
              </Fact>
              <Fact label={field("package_id")}>
                {packages.find((pkg) => pkg.id === agreement.package_id)
                  ?.name ?? "—"}
              </Fact>
              <Fact label={field("contribution_amount")}>
                {agreement.contribution_amount ?? "—"}
              </Fact>
              <Fact label={field("kit_version_id")}>
                {kits.find((kit) => kit.id === agreement.kit_version_id)
                  ?.name ?? "—"}
              </Fact>
              <Fact label={field("kit_shipped_at")}>
                {formatDay(agreement.kit_shipped_at)}
              </Fact>
              <Fact label={field("min_purchase_amount")}>
                {agreement.min_purchase_amount ?? "—"}
              </Fact>
              <Fact label={field("customer_discount_pct")}>
                {check?.discount_pct ?? "—"}
              </Fact>
              <Fact label={field("referrer_reward_pct")}>
                {check?.reward_pct ?? "—"}
              </Fact>
            </div>
            {firstOrder ? (
              <Fact label={translate("tenax.agreements.first_order")}>
                {formatDay(firstOrder.first_order_deadline)}
                <span className="block text-xs text-muted-foreground">
                  {translate("tenax.agreements.first_order_hint")}
                </span>
              </Fact>
            ) : null}
            {agreement.terms ? (
              <Fact label={field("terms")}>
                <span className="whitespace-pre-line">{agreement.terms}</span>
              </Fact>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button asChild>
                <Link to={`/agreements/${agreement.id}`}>
                  {translate("tenax.agreements.edit")}
                </Link>
              </Button>
              {agreement.drive_url ? (
                <Button variant="outline" asChild>
                  <a
                    href={agreement.drive_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {translate("tenax.agreements.open_drive")}
                  </a>
                </Button>
              ) : null}
            </div>
            <Separator />
            <Obligations agreementId={agreement.id} />
            {freeItems.length ? (
              <>
                <h2 className="text-sm font-semibold">{field("free_items")}</h2>
                <ul className="flex flex-col gap-1 text-sm">
                  {freeItems.map((item) => (
                    <li key={item.id}>
                      {item.description ?? "—"} · {item.qty} × {item.unit_value}{" "}
                      €
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </CardContent>
        </Card>
      </RecordContextProvider>
    </PageFrame>
  );
};

// Obligations, ticked in one tap
const Obligations = ({ agreementId }: { agreementId: Identifier }) => {
  const translate = useTranslate();
  const formatDay = useDay();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const { data: obligations = [] } = useGetList<Obligation & { id: number }>(
    "agreement_obligations",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "id", order: "ASC" },
      filter: { agreement_id: agreementId },
    },
  );
  const toggle = async (obligation: Obligation & { id: number }) => {
    try {
      await dataProvider.update("agreement_obligations", {
        id: obligation.id,
        data: {
          done_at: obligation.done_at
            ? null
            : new Date().toISOString().slice(0, 10),
        },
        previousData: obligation,
      });
      await queryClient.invalidateQueries({
        queryKey: ["agreement_obligations"],
      });
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
  };
  if (!obligations.length) return null;
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold">
        {translate("resources.agreements.fields.obligations")}
      </h2>
      <ul className="flex flex-col gap-2">
        {obligations.map((obligation) => (
          <li key={obligation.id} className="flex items-start gap-2 text-sm">
            <Checkbox
              id={`obligation-${obligation.id}`}
              checked={!!obligation.done_at}
              onCheckedChange={() => toggle(obligation)}
              className="mt-0.5"
            />
            <label
              htmlFor={`obligation-${obligation.id}`}
              className="flex flex-col"
            >
              <span>{obligation.description}</span>
              <span className="text-xs text-muted-foreground">
                {translate(`tenax.agreements.party.${obligation.party}`)}
                {obligation.due_date
                  ? ` · ${translate("tenax.agreements.due_date")} ${formatDay(obligation.due_date)}`
                  : ""}
              </span>
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
};

export const AgreementCreate = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // memoized: a new object on each render would reset the form
  const defaults = useMemo<AgreementFormValues>(() => {
    const numberParam = (name: string) =>
      searchParams.get(name) ? Number(searchParams.get(name)) : null;
    return {
      type: (searchParams.get("type") as Agreement["type"]) ?? undefined,
      status: "draft",
      renewal: "none",
      company_id: numberParam("company_id"),
      contact_id: numberParam("contact_id"),
      obligations: [],
      free_items: [],
    };
  }, [searchParams]);
  return (
    <PageFrame
      title={translate("tenax.agreements.new")}
      back={<BackLink to="/agreements" />}
    >
      <Card>
        <CardContent>
          <AgreementForm
            id={null}
            defaultValues={defaults}
            onSaved={(newId) => navigate(`/agreements/${newId}/show`)}
          />
        </CardContent>
      </Card>
    </PageFrame>
  );
};

export const AgreementEdit = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: agreement } = useGetOne<Agreement>("agreements", { id: id! });
  const lists = {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "id", order: "ASC" as const },
    filter: { agreement_id: id },
  };
  const obligations = useGetList<Obligation & { id: number }>(
    "agreement_obligations",
    lists,
  );
  const freeItems = useGetList<FreeItem & { id: number }>(
    "agreement_free_items",
    lists,
  );
  const [key] = useState(() => Date.now());
  if (!agreement || obligations.isPending || freeItems.isPending) return null;
  return (
    <PageFrame
      title={translate("tenax.agreements.edit")}
      back={<BackLink to={`/agreements/${id}/show`} />}
    >
      <Card>
        <CardContent>
          <AgreementForm
            key={key}
            id={agreement.id}
            defaultValues={{
              ...agreement,
              obligations: obligations.data ?? [],
              free_items: freeItems.data ?? [],
            }}
            onSaved={(savedId) => navigate(`/agreements/${savedId}/show`)}
          />
        </CardContent>
      </Card>
    </PageFrame>
  );
};
